import WandbLoader from '@wandb/common/components/WandbLoader';
import {
  opArtifactId,
  opArtifactProject,
  opDict,
  opEntityName,
  opProjectEntity,
  opProjectInternalId,
  opProjectName,
} from '@wandb/weave-ui/cg';
import {useValue} from '@wandb/weave-ui/cgreact';
import * as Panel2 from '@wandb/weave-ui/components/Panel2/panel';
import * as React from 'react';
import {RefObject, useState} from 'react';

import {
  useFetchArtifactCollectionTriggersQuery,
  useFetchProjectTriggersQuery,
} from '../../../../generated/graphql';
import {trackCreateTriggerClicked} from '../../../../util/navigation';
import {maybePluralize} from '../../../../util/uihelpers';
import {CreateTriggerModal} from '../../../ArtifactCollectionSettings/CreateTriggerModal';
import * as S from './style';

const inputType = 'artifact' as const;
export type ConfigType = {};
type TriggersOverviewProps = {
  inputRef: RefObject<HTMLDivElement> | null;
};

export const TriggersOverview: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & TriggersOverviewProps
> = props => {
  const [addTriggerModalOpen, setAddTriggerModalOpen] = useState(false);
  const artifactNode = props.input;
  const overviewValue = useValue(
    React.useMemo(() => {
      const projectNode = opArtifactProject({
        artifact: artifactNode,
      });
      return opDict({
        collectionID: opArtifactId({
          artifact: artifactNode,
        }),
        entityName: opEntityName({
          entity: opProjectEntity({project: projectNode}),
        }),
        projectName: opProjectName({
          project: projectNode,
        }),
        projectID: opProjectInternalId({
          project: projectNode,
        }),
      } as any);
    }, [artifactNode])
  );
  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {
      projectName: overviewValue?.result?.projectName,
      entityName: overviewValue?.result?.entityName,
    },
    skip: overviewValue.loading || !overviewValue.result,
  });
  const collectionTriggersQuery = useFetchArtifactCollectionTriggersQuery({
    variables: {
      artifactCollectionID: overviewValue?.result?.collectionID,
    },
    skip: overviewValue.loading || !overviewValue.result,
  });
  if (
    overviewValue.loading ||
    overviewValue.result == null ||
    projectTriggersQuery.loading ||
    collectionTriggersQuery.loading
  ) {
    return <WandbLoader name="artifact-collection-triggers-list" />;
  }

  const projectScopedTriggers =
    projectTriggersQuery?.data?.project?.triggers.filter(t => {
      return (
        t.triggeredAction.__typename === 'QueueJobTriggeredAction' &&
        t.scope.__typename === 'Project'
      );
    }) ?? [];
  const acScopedTriggers =
    collectionTriggersQuery?.data?.artifactCollection?.triggers.edges
      .map(t => {
        return t?.node;
      })
      .filter(
        t => t.triggeredAction.__typename === 'QueueJobTriggeredAction'
      ) ?? [];
  const triggers = [...acScopedTriggers, ...projectScopedTriggers];

  return (
    <>
      {triggers!.length === 0 ? (
        <>
          <S.SettingsOverview>
            <S.SettingsOverviewText>
              Automate actions based on changes in this registered model.
            </S.SettingsOverviewText>
            <S.ClickableText
              onClick={() => {
                setAddTriggerModalOpen(true);
                trackCreateTriggerClicked(
                  'Model registry collection overview section'
                );
              }}>
              New automation
            </S.ClickableText>
          </S.SettingsOverview>
          {addTriggerModalOpen && (
            <CreateTriggerModal
              open={addTriggerModalOpen}
              onClose={() => setAddTriggerModalOpen(false)}
              projectName={overviewValue.result.projectName}
              entityName={overviewValue.result.entityName}
              projectID={overviewValue.result.projectID}
              artifactCollectionID={overviewValue.result.collectionID}
            />
          )}
        </>
      ) : (
        <S.SettingsOverview>
          <S.ClickableText
            onClick={() => {
              props.inputRef?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
              });
            }}>
            <S.StyledIcon name="trigger-alt" />
            {maybePluralize(triggers?.length, 'automation')}
          </S.ClickableText>
        </S.SettingsOverview>
      )}
    </>
  );
};
